import React, { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { Provider } from "react-redux";
import PickersProvider from "@edgelogistics/edge-ui/lib/providers/pickers";
import ThemeProvider from "@edgelogistics/edge-ui/lib/providers/theme";
import NotificationProvider from "@edgelogistics/edge-ui/lib/providers/notification";
import { OffersProvider } from './src/context/private/offers/infrastructure';
import { FetchProvider } from './src/context/public/auth/infrastructure/store/fetch-context';
import store from "./src/context/old/redux/store";
import IconsProvider from "@edgelogistics/edge-ui/lib/providers/icons";
import { SingletonHooksContainer } from "react-singleton-hook";
import AppNavigator from "./src/context/old/navigation";
import { svgs } from "./assets/svg";
import Snackbar from "./src/context/old/components/Snackbar";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { COLORS } from "./assets/customColors";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { View, Linking } from "react-native";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useFaviconByTheme } from "./src/context/old/utils/hooks/use-favicon-by-theme";
import { AuthProvider } from "./src/context/public/auth/infrastructure/store/auth-context";
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import { REACT_APP_TRIMBLE_MAPS_API_KEY, REACT_APP_CLARITY_PROJECT_ID, REACT_APP_NEW_RELIC_URL } from "@env";

import global_es from "./src/context/shared/domain/translations/es/global.json";
import global_en from "./src/context/shared/domain/translations/en/global.json";

import "./src/context/shared/infrastructure/ui/assets/tailwind/index.output.css";
import { AppDetailsWrapper } from "./src/context/shared/infrastructure";

i18next.init({
  interpolation: { escapeValue: false },
  lng: "en",
  resources: {
    es: {
      global: global_es
    },
    en: {
      global: global_en
    }
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

Linking.addEventListener('url', (event) => {
  if (event.url.includes('clarity.ms')) {
    Linking.openURL(event.url)
  }
})

TrimbleMaps.setAPIKey(REACT_APP_TRIMBLE_MAPS_API_KEY);

export default function App() {
  useFaviconByTheme();

  useEffect(() => {
    const clarityScript = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${REACT_APP_CLARITY_PROJECT_ID}")
    `
    const clarityScriptTag = document.createElement('script')
    clarityScriptTag.innerHTML = clarityScript
    document.body.appendChild(clarityScriptTag)
  }, [])

  useEffect(() => {
    if (REACT_APP_NEW_RELIC_URL) {
      const newRelicScriptTag = document.createElement('script')
      newRelicScriptTag.src = REACT_APP_NEW_RELIC_URL
      document.head.appendChild(newRelicScriptTag)
    }
  }, [])

  return (
    <I18nextProvider i18n={i18next}>
      <QueryClientProvider client={queryClient}>
        <AppDetailsWrapper>
          <Provider store={store}>
            <AuthProvider>
              <FetchProvider>
                <OffersProvider>
                  <ThemeProvider customColors={COLORS}>
                    <IconsProvider customIcons={svgs}>
                      <NotificationProvider>
                        <SafeAreaProvider>
                          <PickersProvider>
                            <SingletonHooksContainer />
                            <View style={{ flex: 1, overflow: "hidden" }}>
                              <AppNavigator />

                              <Toaster
                                position="bottom-center"
                                toastOptions={{
                                  className: "text-sm max-w-2xl bg-black text-white",
                                  success: {
                                    icon: <MdCheckCircle className="h-5 w-5 text-white" />,
                                  },
                                  error: {
                                    icon: <MdCancel className="h-5 w-5 text-white" />,
                                  },
                                  id: "alert-message",
                                }}
                              />
                            </View>
                            <Snackbar />
                            <ReactQueryDevtools />
                          </PickersProvider>
                        </SafeAreaProvider>
                      </NotificationProvider>
                    </IconsProvider>
                  </ThemeProvider>
                </OffersProvider>
              </FetchProvider>
            </AuthProvider>
          </Provider>
        </AppDetailsWrapper>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
